import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.refs.slideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Handles the 'slideshow:select' event by auto-playing the newly selected slide's
   * video/model (if it has one) and pausing the previously selected one, so a video
   * starts as soon as its thumbnail is picked instead of waiting for a poster-button click.
   * Only reacts to user-initiated selection (thumbnail click, swipe, drag) so programmatic
   * slide changes (variant swap, morph, initial render) don't trigger unexpected playback.
   * @param {SlideshowSelectEvent} event - The slideshow:select event.
   */
  #handleSlideshowSelect = (event) => {
    const { slide, previousIndex, index, userInitiated } = event.detail;
    if (!userInitiated || index === previousIndex) return;

    const previousSlide = this.slideshow?.refs.slides?.[previousIndex];
    this.#getSlideMedia(previousSlide)?.pauseMedia();

    const media = this.#getSlideMedia(slide);
    if (!media) return;

    if (media.getAttribute('data-media-loaded')) {
      media.playMedia();
    } else {
      media.showDeferredMedia();
    }
  };

  /**
   * @param {Element} [slide] - The slideshow-slide element to look for playable media in.
   * @returns {import('./media').DeferredMedia | import('./media').ProductModel | null}
   */
  #getSlideMedia(slide) {
    return slide?.querySelector('deferred-media, product-model') ?? null;
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
